import { css, ThemeProvider } from "@emotion/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";
import type { SnackbarKey } from "notistack";
import { closeSnackbar, SnackbarProvider } from "notistack";
import type React from "react";
import { Suspense, useEffect, useRef } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ErrorDialog } from "./components/ErrorDialog";
import { Button } from "./components/ui/button";
import { Toaster } from "./components/ui/sonner";
import { useAppConfigState } from "./hooks/useAppConfigState";
import { useCatalogState } from "./hooks/useCatalogState";
import { useCheckoutFormState } from "./hooks/useCheckoutFormState";
import { useInventoryState } from "./hooks/useInventoryState";
import { useIsDesktopMediaQuery } from "./hooks/useIsDesktopMediaQuery";
import { useLocaleState } from "./hooks/useLocaleState";
import { useSessionState } from "./hooks/useSessionState";
import { useSiteLayoutState } from "./hooks/useSiteLayoutState";
import { useStorefrontSettingsState } from "./hooks/useStorefrontSettingsState";
import { useTailwindMediaQueries } from "./hooks/useTailwindMediaQueries";
import { useThemeState } from "./hooks/useThemeState";
import { CheckoutCompletePage } from "./pages/CheckoutCompletePage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { CompleteResetPasswordPage } from "./pages/CompleteResetPasswordPage";
import { HomePage } from "./pages/HomePage";
import { InventoryPage } from "./pages/InventoryPage";
import LoadingPage from "./pages/LoadingPage";
import { LoginPage } from "./pages/LoginPage";
import NotFoundPage from "./pages/NotFoundPage";
import { OrdersPage } from "./pages/OrdersPage";
import PrintProductPage from "./pages/PrintProductPage";
import ProductPage from "./pages/ProductPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { ShoppingCartPage } from "./pages/ShoppingCartPage";

// Create a client
export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 15 * 60 * 1000, // 15 minutes (increased from 10)
			retry: (failureCount, error) => {
				// Don't retry on 401/403 errors
				if (
					error instanceof Error &&
					(error.message.includes("Session expired") ||
						error.message.includes("Unauthorized"))
				) {
					return false;
				}
				// Only retry once for other errors
				return failureCount < 1;
			},
			refetchOnWindowFocus: false,
			refetchOnMount: false, // Don't refetch on mount if data is fresh
			refetchOnReconnect: false, // Don't refetch on reconnect if data is fresh
		},
	},
});

const AppContent: React.FC = () => {
	//const errorBoundaryState = useErrorBoundaryState();
	const appConfigState = useAppConfigState();
	const appConfig = appConfigState.get().appConfig;
	const appConfigIsLoading = appConfigState.get().isLoading;
	const checkoutUrl = appConfigState.getCheckoutUrl();
	const localeState = useLocaleState();
	const themeState = useThemeState();
	const settingsState = useStorefrontSettingsState();
	const settings = settingsState.get().settings;
	const isLoadingSettings = settingsState.get().isLoading;
	const separateQuoteRequestsByCatalog =
		settingsState.get().settings?.SeparateQuoteRequestsByCatalog;
	const siteLayoutState = useSiteLayoutState();
	const sessionState = useSessionState();
	const loggedIn = sessionState.get().loggedIn;
	const storefrontSettingsState = useStorefrontSettingsState();
	const isAnonymousAccessEnabled =
		storefrontSettingsState.get().settings?.IsAnonymousAccessEnabled ?? false;
	const isDesktop = useIsDesktopMediaQuery();
	const inventoryState = useInventoryState();

	const quoteRequestFormState = useCheckoutFormState();
	const lookuplocation = quoteRequestFormState.get().lookuplocation;
	const isLoadingLocation = quoteRequestFormState.get().isLoadingLocation;
	//const lookupwarehouse = quoteRequestFormState.get().lookupwarehouse;
	//const isLoadingWarehouse = quoteRequestFormState.get().isLoadingWarehouse;

	const catalogState = useCatalogState();
	const catalogs = catalogState.get().catalogs;
	const isLoadingCatalogs = catalogState.get().isLoadingCatalogs;

	//const checkoutFormState = useCheckoutFormState();
	//let warehouseId = checkoutFormState.get().warehouseIdValue;

	const isDarkMode = themeState.isDark();

	const appCss = css`
        color: ${themeState.get().theme.palette.siteTextPrimary};
        color-scheme:${isDarkMode ? "dark" : "light"};
    `;

	const didInitRef = useRef(false);
	useEffect(() => {
		document.documentElement.classList.toggle("dark", isDarkMode);
	}, [isDarkMode]);
	// set swiper css variable at startup
	useEffect(() => {
		if (didInitRef.current) {
			return;
		}
		didInitRef.current = true;
		document.documentElement.style.setProperty(
			"--swiper-theme-color",
			themeState.isDark() ? "rgba(255,255,255,1)" : "rgba(0,0,0,1)",
		);
		if (!isDesktop) {
			inventoryState.setSidebarExpanded(false);
		}
	}, [inventoryState, isDesktop, themeState]);

	// every minute check to see if the catalogState is expired on reload the list of catalogs
	// MV 2024-04-05 the refresh of the catalogs was causing the popup to close on the product page.
	//useInterval(() => {
	//    if (catalogState.isExpired()) {
	//        catalogState.loadAsync();
	//    }
	//}, 60 * 1000);

	// load the AppConfig and set the theme
	useEffect(() => {
		(async () => {
			try {
				if (!appConfig && !appConfigIsLoading) {
					await appConfigState.loadAsync();
					themeState.set(themeState.get().themeName);
				}
			} catch (error) {
				siteLayoutState.handleErrorAsync(error);
			}
		})();
	}, [
		appConfig,
		appConfigIsLoading,
		appConfigState.loadAsync,
		themeState.get,
		themeState.set,
		siteLayoutState.handleErrorAsync,
	]);

	// Load the StorefrontSettings
	useEffect(() => {
		(async () => {
			try {
				if (appConfig && !settings && !isLoadingSettings) {
					await settingsState.loadAsync();
				}
			} catch (error) {
				siteLayoutState.handleErrorAsync(error);
			}
		})();
	}, [
		appConfig,
		settings,
		isLoadingSettings,
		settingsState.loadAsync,
		siteLayoutState.handleErrorAsync,
	]);

	// Load the Catalogs
	useEffect(() => {
		(async () => {
			try {
				if (
					appConfig &&
					!catalogs &&
					!isLoadingCatalogs &&
					settings &&
					(settings.IsAnonymousAccessEnabled || loggedIn)
				) {
					await catalogState.loadAsync();
				}
			} catch (error) {
				siteLayoutState.handleErrorAsync(error);
			}
		})();
	}, [
		appConfig,
		catalogs,
		isLoadingCatalogs,
		loggedIn,
		settings,
		catalogState.loadAsync,
		siteLayoutState.handleErrorAsync,
	]);

	// Default the Location
	useEffect(() => {
		(async () => {
			try {
				if (catalogs && appConfig && !lookuplocation && !isLoadingLocation) {
					await quoteRequestFormState.loadLocationsAsync();
					const lookuplocation = quoteRequestFormState.get().lookuplocation;
					if (lookuplocation?.length) {
						const currentLocationId =
							quoteRequestFormState.get().locationIdValue;
						const currentWarehouseId =
							quoteRequestFormState.get().warehouseIdValue;
						const currentLocation = lookuplocation.find(
							(l) => l.LocationId === currentLocationId,
						);
						if (currentLocation) {
							if (currentLocation.WarehouseId !== currentWarehouseId) {
								quoteRequestFormState.setWarehouseId(
									currentLocation.WarehouseId,
								);
							}
						} else {
							if (catalogs.length > 0 && catalogs[0].LocationId) {
								const catalog = catalogs[0];
								quoteRequestFormState.setLocationId(catalog.LocationId);
								quoteRequestFormState.setWarehouseId(catalog.WarehouseId);
							} else {
								const firstLocation = lookuplocation[0];
								quoteRequestFormState.setLocationId(firstLocation.LocationId);
								quoteRequestFormState.setWarehouseId(firstLocation.WarehouseId);
							}
						}
					} else {
						quoteRequestFormState.setLocationId("");
						quoteRequestFormState.setWarehouseId("");
					}
				}
			} catch (error) {
				siteLayoutState.handleErrorAsync(error);
			}
		})();
	}, [
		appConfig,
		lookuplocation,
		isLoadingLocation,
		catalogs,
		quoteRequestFormState.get,
		quoteRequestFormState.loadLocationsAsync,
		quoteRequestFormState.setLocationId,
		quoteRequestFormState.setWarehouseId,
		siteLayoutState.handleErrorAsync,
	]);

	const mediaQueries = useTailwindMediaQueries();

	useEffect(() => {
		inventoryState.setSidebarExpanded(mediaQueries.desktop);
	}, [mediaQueries.desktop, inventoryState.setSidebarExpanded]);

	const navigate = useNavigate();
	const location = useLocation();
	const pathname = location.pathname;

	// Remove trailing slashes from URLs
	useEffect(() => {
		const currentPath = pathname;
		if (currentPath.endsWith("/") && currentPath !== "/") {
			const newPath = currentPath.slice(0, -1);
			navigate(newPath, { replace: true });
		}
	}, [navigate, pathname]);

	return (
		<ThemeProvider theme={themeState.getTheme()}>
			<div
				className="App"
				css={appCss}
				data-theme={themeState.get().themeName}
				data-language={localeState.get().language}
			>
				<Suspense fallback={<LoadingPage />}>
					{appConfigState.get().appConfig && settings && (
						<Routes>
							{/*<Route path="about" element={<AboutPage />} />*/}
							<Route path="register" element={<RegisterPage />} />
							<Route path="login" element={<LoginPage />} />
							{!loggedIn && (
								<Route path="reset-password" element={<ResetPasswordPage />} />
							)}
							<Route
								path="complete-reset-password"
								element={
									<CompleteResetPasswordPage type="completeresetpassword" />
								}
							/>
							<Route
								path="complete-registration"
								element={
									<CompleteResetPasswordPage type="completeregistration" />
								}
							/>
							{(isAnonymousAccessEnabled || loggedIn) && (
								<>
									<Route path="/" element={<HomePage />} />
									<Route
										path="inventory/:catalogid/index"
										element={<InventoryPage />}
									/>
									<Route
										path="inventory/:catalogid"
										element={<InventoryPage />}
									/>
									<Route
										path="inventory/:catalogid/:departmentid"
										element={<InventoryPage />}
									/>
									<Route
										path="inventory/:catalogid/:departmentid/:categoryid"
										element={<InventoryPage />}
									/>
									<Route
										path="inventory/:catalogid/:departmentid/:categoryid/:subcategoryid"
										element={<InventoryPage />}
									/>
									<Route
										path="complete-change-password"
										element={
											<CompleteResetPasswordPage type="completechangepassword" />
										}
									/>
									<Route
										path="product/:productid/warehouseid/:warehouseid/locationid/:locationid/fromdate/:fromdate/todate/:todate"
										element={<ProductPage />}
									/>
									<Route
										path="printproduct/:productid/warehouseid/:warehouseid/locationid/:locationid/fromdate/:fromdate/todate/:todate"
										element={<PrintProductPage />}
									/>
									<Route path="shoppingcart" element={<ShoppingCartPage />} />
									<Route path="orders" element={<OrdersPage />} />
									<Route
										path="checkoutcomplete"
										element={<CheckoutCompletePage />}
									/>
									{!separateQuoteRequestsByCatalog && (
										<Route path={checkoutUrl} element={<CheckoutPage />} />
									)}
									{separateQuoteRequestsByCatalog && (
										<Route
											path={`${checkoutUrl}/catalogid/:catalogid`}
											element={<CheckoutPage />}
										/>
									)}
								</>
							)}
							{(isAnonymousAccessEnabled || loggedIn) && (
								<Route path="*" element={<NotFoundPage />} />
							)}
							{!isAnonymousAccessEnabled && !loggedIn && (
								<Route path="*" element={<LoginPage />} />
							)}
							<Route path="*" element={<NotFoundPage />} />
						</Routes>
					)}
					{(!appConfigState.get().appConfig || !settings) && (
						<Routes>
							<Route path="*" element={<LoadingPage />} />
						</Routes>
					)}
				</Suspense>
				{siteLayoutState.get().errorMessage && (
					<AnimatePresence>
						<motion.div layoutId="errordialog">
							<ErrorDialog />
						</motion.div>
					</AnimatePresence>
				)}
				<SnackbarProvider
					action={(snackbarId: SnackbarKey) => (
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label="Dismiss"
							onClick={async () => {
								return Promise.resolve().then(() => {
									closeSnackbar(snackbarId);
								});
							}}
						>
							<XIcon size={16} />
						</Button>
					)}
				/>
				<Toaster />
			</div>
		</ThemeProvider>
	);
};

const App: React.FC = () => {
	return (
		<QueryClientProvider client={queryClient}>
			<AppContent />
		</QueryClientProvider>
	);
};

export default App;
